import { useQuery } from '@tanstack/react-query'
import { useConexaQuery } from '@/hooks/useConexaQuery'
import {
  getScheduledConsultations,
  getPerformedConsultations,
  getCancelledConsultations,
  getGroupConsultations,
  getReservationAttendances,
  getConsultationDetails,
} from './my-calls-services'
import { IConsultation } from './types'

type ConsultationType = 'scheduled' | 'performed' | 'cancelled'

export const useConsultations = (type: ConsultationType) => {
  const queryFnMap: Record<ConsultationType, () => Promise<IConsultation[]>> = {
    scheduled: getScheduledConsultations,
    performed: getPerformedConsultations,
    cancelled: getCancelledConsultations,
  }

  return useQuery(['consultation-list', type], {
    queryFn: async () => {
      const fn = queryFnMap[type]

      if (!fn) {
        throw new Error(
          'Use one of the consultation types: `scheduled`, `performed` or `cancelled`',
        )
      }

      const consultations = await fn()

      return consultations
    },
    refetchOnWindowFocus: false,
  })
}

export const useGroupConsultations = () => {
  return useQuery(['group-consultation-list'], {
    queryFn: async () => {
      const groupConsultations = await getGroupConsultations()

      return groupConsultations
    },
    refetchOnWindowFocus: false,
  })
}

export const useReservationsConsultations = () => {
  return useQuery(['reservation-consultation-list'], {
    queryFn: async () => {
      const reservationConsultations = await getReservationAttendances()

      return reservationConsultations
    },
    refetchOnWindowFocus: false,
  })
}

export const useConsultationDetails = (consultationId: string | number) => {
  return useConexaQuery({
    queryKey: ['get-consultation-details', consultationId],
    service: () => getConsultationDetails(String(consultationId)),
    config: {
      enabled: Boolean(consultationId),
      refetchOnWindowFocus: false,
    },
  })
}
